import { Pencil, Trash2 } from "lucide-react";
import { euro, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CATEGORIE, catOf } from "@/components/spese/categorie";

const sum = (list) => list.reduce((a, s) => a + Number(s.importo || 0), 0);

const Row = ({ s, onEdit, onDelete }) => (
  <div className="flex items-center gap-3 px-4 py-2.5 group hover:bg-secondary/40 transition-colors" data-testid={`expense-row-${s.id}`}>
    <div className="w-16 shrink-0 text-xs text-muted-foreground tabular-nums">{s.data ? fmtDate(s.data) : "—"}</div>
    <div className="flex-1 min-w-0">
      <div className="font-medium truncate">{s.descrizione || s.fornitore || s.categoria}</div>
      {s.fornitore && s.descrizione && <div className="text-xs text-muted-foreground truncate">{s.fornitore}</div>}
    </div>
    <div className="font-semibold text-sm whitespace-nowrap tabular-nums">{euro(s.importo)}</div>
    <div className="flex gap-0.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(s)} data-testid={`expense-edit-${s.id}`}><Pencil className="w-4 h-4" /></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => onDelete(s)} data-testid={`expense-delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  </div>
);

// Spese raggruppate per categoria: gruppi apribili, totale sempre visibile, voci dalla più recente.
export const ExpenseGroups = ({ spese, open, onOpenChange, onEdit, onDelete }) => {
  const groups = CATEGORIE.map((c) => ({
    ...c,
    items: spese.filter((s) => catOf(s.categoria).key === c.key).sort((a, b) => (b.data || "").localeCompare(a.data || "")),
  })).filter((g) => g.items.length);
  const grand = sum(spese);

  if (!groups.length) return <div className="rounded-xl border border-border bg-card text-center py-12 text-muted-foreground" data-testid="spese-list">Nessuna spesa registrata.</div>;
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="spese-list">
      <Accordion type="multiple" value={open} onValueChange={onOpenChange}>
        {groups.map((g) => (
          <AccordionItem key={g.key} value={g.key} className="border-border last:border-b-0" data-testid={`expense-group-${g.key}`}>
            <AccordionTrigger className="px-4 py-3.5 hover:no-underline hover:bg-secondary/40 gap-3" data-testid={`expense-group-toggle-${g.key}`}>
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${g.tone}`}><g.icon className="w-5 h-5" /></span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-[15px] truncate">{g.key}</span>
                <span className="block text-xs text-muted-foreground font-normal">{g.items.length} {g.items.length === 1 ? "spesa" : "spese"} · ultima {g.items[0].data ? fmtDate(g.items[0].data) : "—"}</span>
              </span>
              <span className="text-right shrink-0">
                <span className="block font-heading font-bold text-base tabular-nums" data-testid={`expense-group-total-${g.key}`}>{euro(sum(g.items))}</span>
                <span className="block text-[11px] text-muted-foreground font-normal">{grand ? Math.round((sum(g.items) / grand) * 100) : 0}% del totale</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-1">
              <div className="divide-y divide-border border-t border-border bg-secondary/10">
                {g.items.map((s) => <Row key={s.id} s={s} onEdit={onEdit} onDelete={onDelete} />)}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export default ExpenseGroups;
